<template>
	<div class="active-projects-widget">
		<div class="active-projects-widget__header">
			<h3 class="active-projects-widget__title">Active Projects</h3>
			<themed-tooltip 
				placement="bottom"
				:show-delay="300"
				max-width="300px"
			>
				<button 
					class="active-projects-widget__info" 
					aria-label="More information about Active Projects"
					@mouseenter="loadTooltip('dashboard-activeprojects')"
				>
					<svg 
						width="14" 
						height="14" 
						viewBox="0 0 24 24" 
						fill="none" 
						stroke="currentColor" 
						stroke-width="2" 
						stroke-linecap="round" 
						stroke-linejoin="round"
					>
						<circle cx="12" cy="12" r="10"></circle>
						<line x1="12" y1="16" x2="12" y2="12"></line>
						<line x1="12" y1="8" x2="12.01" y2="8"></line>
					</svg>
				</button>
				<template #content>
					<div v-html="getTooltipContent('dashboard-activeprojects')"></div>
				</template>
			</themed-tooltip>
		</div>
		
		<div class="active-projects-widget__content">
			<div class="active-projects-widget__chart">
				<!-- <fusioncharts
					type="doughnut2d"
					width="100%"
					height="220"
					:dataSource="chartDataSource"
				></fusioncharts> -->



				<theme-doughnut-chart
					:data="doughnutChartData"
					:colors="chartColors"
					:show-percentages="false"
					:show-legend="false"
					cutout="70%"
				/>

				<div class="active-projects-widget__chart-center">
					<div class="active-projects-widget__chart-value">{{ totalProjects }}</div>
					<div class="active-projects-widget__chart-label">TOTAL</div>
				</div>
			</div>
			
			<div class="active-projects-widget__legend">
				<div 
					v-for="(item, index) in legendItems" 
					:key="index"
					class="active-projects-widget__legend-item"
				>
					<span class="active-projects-widget__legend-dot" :style="{ backgroundColor: item.color }"></span>
					<span class="active-projects-widget__legend-label">{{ item.label }}</span>
					<span class="active-projects-widget__legend-value">{{ item.value }} {{ item.value === 1 ? 'project' : 'projects' }}</span>
					<span class="active-projects-widget__legend-percent">{{ item.percent }}%</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import ThemedTooltip from '../ThemedTooltip.vue';
import axios from 'axios';
import ThemeDoughnutChart from '../charts/ThemeDoughnutChart.vue';

export default {
	name: 'ActiveProjectsWidget',
	components: {
		ThemedTooltip,
		ThemeDoughnutChart
	},
	props: {
		chartData: {
			type: [String, Array],
			required: true
		}
	},
	data() {
		return {
			tooltipCache: {}
		}
	},
	computed: {
		parsedData() {
			if (typeof this.chartData === 'string') {
				try {
					return JSON.parse(this.chartData);
				} catch (e) {
					return [];
				}
			}
			return this.chartData || [];
		},
		totalProjects() {
			return _.sumBy(this.parsedData, 'value') || 0;
		},
		chartDataSource() {
			const colors = ['22c55e', '3b82f6', 'f59e0b', '8b5cf6', 'ec4899', '14b8a6'];
			
			return {
				chart: {
					bgColor: '#FFFFFF',
					bgAlpha: '0',
					canvasBgColor: '#FFFFFF',
					canvasBgAlpha: '0',
					baseFont: '"-apple-system", "BlinkMacSystemFont", "Segoe UI", "Roboto", "Helvetica Neue", "Arial", "Noto Sans", "sans-serif"',
					baseFontSize: 14,
					caption: '',
					showLabels: '0',
					showValues: '0',
					showPercentValues: '0',
					showLegend: '0',
					enableSmartLabels: '0',
					startingAngle: '90',
					use3DLighting: '0',
					theme: 'fusion',
					defaultCenterLabel: '',
					centerLabel: '',
					doughnutRadius: '70',
					pieRadius: '85',
					paletteColors: colors.join(',')
				},
				data: this.parsedData.map((item, index) => ({
					label: item.label,
					value: item.value,
					color: colors[index % colors.length]
				}))
			}
		},
		chartColors() {
			return ['#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6'];
		},
		doughnutChartData() {
			// Data formatted for ThemeDoughnutChart component
			return this.parsedData.map(item => ({
				label: item.label,
				value: item.value
			}));
		},
		legendItems() {
			const colors = this.chartColors;
			
			return this.parsedData.map((item, index) => ({
				label: item.label,
				value: item.value,
				percent: this.totalProjects > 0 
					? Math.round((item.value / this.totalProjects) * 100)
					: 0,
				color: colors[index % colors.length]
			}));
		}
	},
	methods: {
		getTooltipContent(identifier) {
			if (this.tooltipCache[identifier]) {
				return this.tooltipCache[identifier];
			}
			return '<div style="display: flex; align-items: center; gap: 0.5rem;"><svg class="animate-spin" style="width: 1rem; height: 1rem;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Loading...</span></div>';
		},
		async loadTooltip(identifier) {
			if (!this.tooltipCache[identifier]) {
				this.$set(this.tooltipCache, identifier, '<div style="display: flex; align-items: center; gap: 0.5rem;"><svg class="animate-spin" style="width: 1rem; height: 1rem;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Loading...</span></div>');
				
				try {
					const response = await axios.get(`/api/tooltips/identifier/${identifier}`);
					this.$set(this.tooltipCache, identifier, response.data.content || 'Tooltip content not available');
				} catch (err) {
					console.error('Error loading tooltip:', err);
					if (err.response && err.response.status === 404) {
						this.$set(this.tooltipCache, identifier, 'Tooltip content is not yet available for this field.');
					} else {
						this.$set(this.tooltipCache, identifier, 'Failed to load information');
					}
				}
			}
		}
	}
}
</script>
